import React, { useEffect, useMemo, useState, useCallback } from "react";
import ProductImage from "../Shop/ProductImage";
import { CATEGORY_MAP } from "../../utils/categorymapping";

/**
 * ============================================================
 * GUIDE — CLÉS LOCALES
 * ============================================================
 *
 * Les catégories classiques et cave sont séparées.
 */
const LS_KEYS = {
  classic: "mdx_dynamic_categories_v1",
  cave: "mdx_dynamic_categories_cave_v1",
};

const PRODUCT_RECO_SAVE_ENDPOINT =
  "https://marchedeluxe.ch/services/save_product_recommendations.php";

const PRODUCT_RECO_GET_ENDPOINT =
  "https://marchedeluxe.ch/services/get_product_recommendations_admin.php";

const normalizeCat = (v) =>
  String(v || "")
    .trim()
    .replace(/\s+/g, " ");

const getScopeKey = (scope) =>
  scope === "cave" ? LS_KEYS.cave : LS_KEYS.classic;

const loadDynamicCats = (scope = "classic") => {
  try {
    const raw = localStorage.getItem(getScopeKey(scope));
    const arr = JSON.parse(raw || "[]");
    return Array.isArray(arr) ? arr.map(normalizeCat).filter(Boolean) : [];
  } catch {
    return [];
  }
};

const saveDynamicCat = (cat, scope = "classic") => {
  const c = normalizeCat(cat);
  if (!c) return;

  const base = new Set(loadDynamicCats(scope));
  base.add(c);

  try {
    localStorage.setItem(getScopeKey(scope), JSON.stringify(Array.from(base)));
  } catch {
    // ignore
  }
};

/**
 * ============================================================
 * GUIDE — DÉTECTION PRODUIT LAITIER / FROMAGE
 * ============================================================
 *
 * Sert uniquement à masquer les champs épicerie.
 * Le lait animal est géré dans AdminMilkTypes.
 */
const isDairyCategory = (value) => {
  const c = String(value || "")
    .trim()
    .toLowerCase();

  return (
    c.includes("fromage") ||
    c.includes("vache") ||
    c.includes("brebis") ||
    c.includes("chèvre") ||
    c.includes("chevre") ||
    c.includes("lait")
  );
};

/**
 * ============================================================
 * GUIDE — HELPERS PRIX
 * ============================================================
 */
const toCents = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
};

const centsToInput = (cents) => {
  const n = Number(cents ?? 0);
  if (!Number.isFinite(n)) return "";
  return (n / 100).toFixed(2);
};

/**
 * ============================================================
 * GUIDE — détecter le scope
 * ============================================================
 *
 * Priorité :
 * 1. prop productScope venant du parent
 * 2. métadonnées déjà présentes sur le produit
 */
function detectScope(formP, productScope = "classic") {
  if (productScope === "cave") return "cave";
  if (!formP) return "classic";

  if (formP.__productScope === "cave") return "cave";
  if (formP.product_scope === "cave") return "cave";
  if (formP.__isCave === true) return "cave";
  if (Number(formP.isCave || 0) === 1) return "cave";
  if (formP.source === "cave") return "cave";

  return "classic";
}

/**
 * ============================================================
 * GUIDE — NORMALISATION SLOT RECOMMANDATION
 * ============================================================
 *
 * Format attendu :
 * - classic:294
 * - cave:301
 */
const normalizeRecoSlot = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

/**
 * ============================================================
 * GUIDE — PARSING SLOT RECOMMANDATION
 * ============================================================
 */
const parseRecoSlot = (value) => {
  const raw = normalizeRecoSlot(value);
  if (!raw) return null;

  const match = raw.match(/^(classic|cave):(\d+)$/i);
  if (!match) return null;

  const scope = match[1].toLowerCase();
  const productId = Number(match[2] || 0);

  if (!productId) return null;

  return {
    scope,
    product_id: productId,
  };
};

/**
 * ============================================================
 * GUIDE — SAUVEGARDE DES RECOMMANDATIONS
 * ============================================================
 *
 * Remplace l’état actuel des 2 recommandations d’un produit source.
 */
async function saveProductRecommendations({
  sourceProductId,
  sourceScope,
  slot1,
  slot2,
}) {
  const parsed = [parseRecoSlot(slot1), parseRecoSlot(slot2)].filter(Boolean);

  const res = await fetch(PRODUCT_RECO_SAVE_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      source_product_id: sourceProductId,
      source_scope: sourceScope,
      recommendations: parsed,
    }),
  });

  const data = await res.json();

  if (!res.ok || !data?.success) {
    throw new Error(
      data?.message ||
        data?.error ||
        "Impossible de sauvegarder les produits recommandés."
    );
  }
}

/**
 * ============================================================
 * GUIDE — CHARGEMENT DES RECOMMANDATIONS
 * ============================================================
 *
 * Cette V1 recharge les 2 slots admin sous forme texte :
 * - slot1
 * - slot2
 */
async function loadProductRecommendations({ sourceProductId, sourceScope }) {
  const url = `${PRODUCT_RECO_GET_ENDPOINT}?source_product_id=${encodeURIComponent(
    sourceProductId
  )}&source_scope=${encodeURIComponent(sourceScope)}`;

  const res = await fetch(url, {
    method: "GET",
  });

  const data = await res.json();

  if (!res.ok || !data?.success) {
    throw new Error(
      data?.message ||
        data?.error ||
        "Impossible de charger les produits recommandés."
    );
  }

  return {
    slot1: data?.slot1 || "",
    slot2: data?.slot2 || "",
  };
}

export default function EditProductModal({
  formP,
  setFormP,
  handleSave,
  isSubmitting,
  closeModal,
  /**
   * ============================================================
   * GUIDE — productScope
   * ============================================================
   *
   * Reçu depuis App.js.
   * Très important pour ne pas perdre le mode cave.
   */
  productScope = "classic",
}) {
  const [dynamicCats, setDynamicCats] = useState([]);

  const scope = useMemo(
    () => detectScope(formP, productScope),
    [formP, productScope]
  );

  const isCaveMode = scope === "cave";

  /**
   * ============================================================
   * GUIDE — charge les catégories du bon scope
   * ============================================================
   */
  useEffect(() => {
    if (!formP) return;
    setDynamicCats(loadDynamicCats(scope));
  }, [formP, scope]);

  /**
   * ============================================================
   * GUIDE — charge les recommandations existantes
   * ============================================================
   */
  useEffect(() => {
    let isMounted = true;

    const loadRecoSlots = async () => {
      if (!formP?.id) return;

      try {
        const data = await loadProductRecommendations({
          sourceProductId: formP.id,
          sourceScope: scope,
        });

        if (!isMounted) return;

        setFormP((prev) => ({
          ...prev,
          recommendationSlot1: data.slot1 || "",
          recommendationSlot2: data.slot2 || "",
        }));
      } catch {
        if (!isMounted) return;

        setFormP((prev) => ({
          ...prev,
          recommendationSlot1: prev?.recommendationSlot1 || "",
          recommendationSlot2: prev?.recommendationSlot2 || "",
        }));
      }
    };

    loadRecoSlots();

    return () => {
      isMounted = false;
    };
  }, [formP?.id, scope, setFormP]);

  /**
   * ============================================================
   * GUIDE — aperçu image
   * ============================================================
   *
   * Si une nouvelle image est choisie, on utilise l'URL temporaire.
   * Sinon, on conserve l’image existante.
   */
  const previewProduct = useMemo(() => {
    if (formP?.imagePreviewUrl) {
      return { ...formP, image: formP.imagePreviewUrl };
    }

    return {
      ...formP,
      image:
        formP?.image ||
        formP?.image_url ||
        formP?.imageUrl ||
        formP?.urlImage ||
        "",
      imageUrl:
        formP?.imageUrl ||
        formP?.image_url ||
        formP?.image ||
        formP?.urlImage ||
        "",
    };
  }, [formP]);

  /**
   * ============================================================
   * GUIDE — nettoyage URL preview
   * ============================================================
   */
  useEffect(() => {
    const url = formP?.imagePreviewUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [formP?.imagePreviewUrl]);

  /**
   * ============================================================
   * GUIDE — sélection image
   * ============================================================
   */
  const handlePickImage = (file) => {
    if (!file) return;
    if (formP?.imagePreviewUrl) URL.revokeObjectURL(formP.imagePreviewUrl);
    const previewUrl = URL.createObjectURL(file);
    setFormP({ ...formP, imageFile: file, imagePreviewUrl: previewUrl });
  };

  /**
   * ============================================================
   * GUIDE — fermeture modale
   * ============================================================
   */
  const handleClose = useCallback(() => {
    if (formP?.imagePreviewUrl) URL.revokeObjectURL(formP.imagePreviewUrl);
    closeModal?.();
  }, [closeModal, formP?.imagePreviewUrl]);

  /**
   * ============================================================
   * GUIDE — catégories affichées
   * ============================================================
   */
  const categories = useMemo(() => {
    const fromMap = isCaveMode
      ? []
      : Object.keys(CATEGORY_MAP || {}).map(normalizeCat);

    const dyn = (dynamicCats || []).map(normalizeCat);

    const current =
      formP?.category && formP.category !== "NEW"
        ? [normalizeCat(formP.category)]
        : [];

    const extraNew = formP?.newCategoryName
      ? [normalizeCat(formP.newCategoryName)]
      : [];

    const all = new Set([...fromMap, ...dyn, ...current, ...extraNew]);

    return Array.from(all)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "fr"));
  }, [dynamicCats, formP?.category, formP?.newCategoryName, isCaveMode]);

  /**
   * ============================================================
   * GUIDE — catégorie finale courante
   * ============================================================
   */
  const currentFinalCategory = useMemo(() => {
    return formP?.category === "NEW" ? formP?.newCategoryName : formP?.category;
  }, [formP?.category, formP?.newCategoryName]);

  /**
   * ============================================================
   * GUIDE — affichage champs épicerie
   * ============================================================
   */
  const showEpicerieFields = useMemo(() => {
    if (isCaveMode) return false;
    return !isDairyCategory(currentFinalCategory);
  }, [currentFinalCategory, isCaveMode]);

  /**
   * ============================================================
   * GUIDE — valeurs prix pour inputs
   * ============================================================
   */
  const oldPriceInput = useMemo(
    () => centsToInput(formP?.old_price),
    [formP?.old_price]
  );

  const priceInput = useMemo(() => centsToInput(formP?.price), [formP?.price]);

  /**
   * ============================================================
   * GUIDE — save wrapper
   * ============================================================
   *
   * On construit nextForm et on le passe directement au parent,
   * pour éviter le problème de setState asynchrone.
   */
  const onSaveWrapped = useCallback(async () => {
    if (!formP) return;

    const finalCategory =
      formP.category === "NEW" ? formP.newCategoryName : formP.category;

    const cat = normalizeCat(finalCategory);
    if (!cat) {
      alert("Veuillez choisir une catégorie.");
      return;
    }

    saveDynamicCat(cat, scope);
    setDynamicCats(loadDynamicCats(scope));

    const existingImageUrl =
      formP.image_url ||
      formP.imageUrl ||
      formP.image ||
      formP.urlImage ||
      "";

    const nextForm = {
      ...formP,
      category: cat,

      /**
       * ========================================================
       * GUIDE — IMAGE EXISTANTE
       * ========================================================
       *
       * Important :
       * si aucun nouveau fichier n’est sélectionné, on conserve
       * l’image actuelle pour éviter qu’elle disparaisse.
       */
      image_url: existingImageUrl,
      imageUrl: formP.imageUrl || existingImageUrl,
      image: formP.image || existingImageUrl,

      /**
       * ========================================================
       * GUIDE — CHAMPS ÉPICERIE / CLASSIQUE
       * ========================================================
       */
      origine: formP.origine || "",
      texture: formP.texture || "",
      intensite: formP.intensite || "",

      /**
       * ========================================================
       * GUIDE — CHAMPS CAVE
       * ========================================================
       */
      millesime: isCaveMode ? formP.millesime || "" : "",
      region: isCaveMode ? formP.region || "" : "",
      cepage: isCaveMode ? formP.cepage || "" : "",

      __productScope: isCaveMode ? "cave" : "classic",
      __isCave: isCaveMode,
      product_scope: isCaveMode ? "cave" : "classic",
    };

    setFormP(nextForm);

    /**
     * Très important :
     * on passe nextForm directement au parent.
     */
    await handleSave?.(nextForm);

    /**
     * Puis on sauvegarde les recommandations relationnelles.
     */
    if (nextForm?.id) {
      await saveProductRecommendations({
        sourceProductId: nextForm.id,
        sourceScope: isCaveMode ? "cave" : "classic",
        slot1: nextForm.recommendationSlot1,
        slot2: nextForm.recommendationSlot2,
      });
    }
  }, [
    formP,
    handleSave,
    isCaveMode,
    scope,
    setFormP,
  ]);

  if (!formP) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-content admin-modal"
        style={{ maxWidth: "500px", width: "90%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>
          {isCaveMode ? "🍷 Modifier produit cave :" : "✏️ Modifier :"}{" "}
          {formP.name}
        </h2>

        {/* =====================================================
            BLOC APERÇU IMAGE
            ===================================================== */}
        <div style={{ marginTop: 12, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: "bold", marginBottom: 8 }}>
            Aperçu
          </div>

          <div
            style={{
              width: "100%",
              height: 180,
              border: "1px solid #eee",
              borderRadius: 6,
              background: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <ProductImage
              product={previewProduct}
              className=""
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
        </div>

        {/* =====================================================
            BLOC FORMULAIRE
            ===================================================== */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          {/* =====================================================
              NOM PRODUIT
              ===================================================== */}
          <label style={{ fontSize: "12px", fontWeight: "bold" }}>
            Nom du produit
          </label>
          <input
            type="text"
            className="search-input"
            value={formP.name || ""}
            onChange={(e) => setFormP({ ...formP, name: e.target.value })}
          />

          {/* =====================================================
              CATÉGORIE
              ===================================================== */}
          <label style={{ fontSize: "12px", fontWeight: "bold" }}>
            Catégorie
          </label>
          <select
            className="search-input"
            value={formP.category || ""}
            onChange={(e) =>
              setFormP({
                ...formP,
                category: e.target.value,
              })
            }
          >
            <option value="">-- Sélectionner --</option>

            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}

            <option value="NEW">➕ + Nouvelle catégorie...</option>
          </select>

          {/* =====================================================
              NOUVELLE CATÉGORIE
              ===================================================== */}
          {formP.category === "NEW" && (
            <input
              type="text"
              placeholder="Nom de la nouvelle catégorie"
              className="search-input"
              style={{ border: "2px solid #d4af37" }}
              value={formP.newCategoryName || ""}
              onChange={(e) =>
                setFormP({
                  ...formP,
                  newCategoryName: e.target.value,
                })
              }
            />
          )}

          {/* =====================================================
              CHAMPS CAVE
              ===================================================== */}
          {isCaveMode && (
            <>
              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Millésime
              </label>
              <input
                type="text"
                placeholder="Ex: 2022"
                className="search-input"
                value={formP.millesime || ""}
                onChange={(e) =>
                  setFormP({ ...formP, millesime: e.target.value })
                }
              />

              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Région
              </label>
              <input
                type="text"
                placeholder="Ex: Valais"
                className="search-input"
                value={formP.region || ""}
                onChange={(e) =>
                  setFormP({ ...formP, region: e.target.value })
                }
              />

              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Cépage
              </label>
              <input
                type="text"
                placeholder="Ex: Pinot noir"
                className="search-input"
                value={formP.cepage || ""}
                onChange={(e) =>
                  setFormP({ ...formP, cepage: e.target.value })
                }
              />
            </>
          )}

          {/* =====================================================
              CHAMPS ÉPICERIE / CLASSIQUE NON LAITIER
              ===================================================== */}
          {showEpicerieFields && (
            <>
              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Origine
              </label>
              <input
                type="text"
                placeholder="Ex: Italie"
                className="search-input"
                value={formP.origine || ""}
                onChange={(e) =>
                  setFormP({ ...formP, origine: e.target.value })
                }
              />

              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Texture
              </label>
              <input
                type="text"
                placeholder="Ex: Croquant"
                className="search-input"
                value={formP.texture || ""}
                onChange={(e) =>
                  setFormP({ ...formP, texture: e.target.value })
                }
              />

              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Intensité
              </label>
              <input
                type="text"
                placeholder="Ex: Doux"
                className="search-input"
                value={formP.intensite || ""}
                onChange={(e) =>
                  setFormP({ ...formP, intensite: e.target.value })
                }
              />
            </>
          )}

          {/* =====================================================
              PRIX
              ===================================================== */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "12px" }}>Prix de base (CHF)</label>
              <input
                type="number"
                className="search-input"
                value={oldPriceInput}
                onChange={(e) =>
                  setFormP({ ...formP, old_price: toCents(e.target.value) })
                }
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "12px" }}>Prix Promo (CHF)</label>
              <input
                type="number"
                className="search-input"
                value={priceInput}
                onChange={(e) =>
                  setFormP({ ...formP, price: toCents(e.target.value) })
                }
              />
            </div>
          </div>

          {/* =====================================================
              STOCK + UNITÉ
              ===================================================== */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "12px" }}>Stock</label>
              <input
                type="number"
                className="search-input"
                value={formP.stock || ""}
                onChange={(e) => setFormP({ ...formP, stock: e.target.value })}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "12px" }}>Unité</label>
              <input
                type="text"
                className="search-input"
                value={formP.unite || "KG"}
                onChange={(e) => setFormP({ ...formP, unite: e.target.value })}
              />
            </div>
          </div>

          {/* =====================================================
              HISTOIRE PRODUIT
              ===================================================== */}
          <label style={{ fontSize: "12px" }}>Histoire du produit</label>
          <textarea
            className="search-input"
            style={{ height: "60px" }}
            value={formP.histoire || ""}
            onChange={(e) => setFormP({ ...formP, histoire: e.target.value })}
          />

          {/* =====================================================
              PRODUITS RECOMMANDÉS
              ===================================================== */}
          <label style={{ fontSize: "12px", fontWeight: "bold" }}>
            Produits recommandés
          </label>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "10px",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: "8px",
              background: "#faf8f3",
            }}
          >
            <div
              style={{ fontSize: "12px", lineHeight: 1.5, color: "#5c5247" }}
            >
              Format attendu : <strong>classic:294</strong> ou{" "}
              <strong>cave:301</strong>
            </div>

            <input
              type="text"
              placeholder="Produit recommandé #1 ex: classic:294"
              className="search-input"
              value={formP.recommendationSlot1 || ""}
              onChange={(e) =>
                setFormP({ ...formP, recommendationSlot1: e.target.value })
              }
            />

            <input
              type="text"
              placeholder="Produit recommandé #2 ex: cave:301"
              className="search-input"
              value={formP.recommendationSlot2 || ""}
              onChange={(e) =>
                setFormP({ ...formP, recommendationSlot2: e.target.value })
              }
            />
          </div>

          {/* =====================================================
              IMAGE
              ===================================================== */}
          <label style={{ fontSize: "12px" }}>Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handlePickImage(e.target.files?.[0])}
          />

          {/* =====================================================
              STATUTS
              ===================================================== */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              marginTop: "10px",
            }}
          >
            <label style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={Number(formP.isOnline) === 1}
                onChange={(e) =>
                  setFormP({ ...formP, isOnline: e.target.checked ? 1 : 0 })
                }
              />{" "}
              En ligne
            </label>

            <label style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={Number(formP.isDiscount) === 1}
                onChange={(e) =>
                  setFormP({ ...formP, isDiscount: e.target.checked ? 1 : 0 })
                }
              />{" "}
              Promo
            </label>
          </div>
        </div>

        {/* =====================================================
            ACTIONS MODALE
            ===================================================== */}
        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
          <button
            onClick={onSaveWrapped}
            className="add-btn"
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? "Envoi..." : "💾 Sauvegarder"}
          </button>

          <button
            onClick={handleClose}
            className="btn-luxury-outline"
            disabled={isSubmitting}
            type="button"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}